import React from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, SparklesIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';
import { useNavigate } from 'react-router-dom';

interface SubscriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    message?: string;
}

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ isOpen, onClose, message }) => {
    const navigate = useNavigate();

    const handleUpgrade = () => {
        onClose();
        navigate('/subscription-pricing');
    };

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-2xl shadow-2xl border border-orange-100 overflow-hidden animate-in zoom-in-95 duration-200">
                    
                    {/* Header with Gradient */}
                    <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
                        <div className="absolute bottom-0 left-0 -ml-8 -mb-8 w-32 h-32 bg-black/10 rounded-full blur-2xl pointer-events-none"></div>
                        
                        <div className="relative z-10 flex flex-col items-center">
                            <div className="h-12 w-12 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center mb-3 shadow-inner border border-white/30">
                                <SparklesIcon className="h-7 w-7 text-white" />
                            </div>
                            <Dialog.Title className="text-xl font-bold tracking-tight">Upgrade Your Plan</Dialog.Title>
                            <p className="text-white/90 text-sm mt-1 font-medium">Unlock More Tokens & Projects</p>
                        </div>

                        <button 
                            onClick={onClose}
                            className="absolute top-4 right-4 text-white/70 hover:text-white hover:bg-white/20 rounded-full p-1 transition-all"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>

                    <div className="p-6">
                        {/* Error Message Context */}
                        <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 mb-6">
                            <p className="text-orange-800 text-sm font-medium text-center leading-relaxed">
                                {message || "You've reached the limits of your Starter plan."}
                            </p>
                        </div>

                        {/* Benefits List */}
                        <div className="space-y-3 mb-8">
                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Upgrade Benefits:</h4>
                            <div className="flex items-start gap-3">
                                <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                                <span className="text-gray-600 text-sm">Up to 5,000,000 AI Tokens</span>
                            </div>
                            <div className="flex items-start gap-3">
                                <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                                <span className="text-gray-600 text-sm">More Daily Limit Allocation</span>
                            </div>
                            <div className="flex items-start gap-3">
                                <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                                <span className="text-gray-600 text-sm">Unlimited Projects Available</span>
                            </div>
                            <div className="flex items-start gap-3">
                                <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                                <span className="text-gray-600 text-sm">Priority Support & Updates</span>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="space-y-3">
                            <Button 
                                onClick={handleUpgrade}
                                className="w-full h-12 text-base bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white shadow-lg shadow-orange-500/25"
                            >
                                Upgrade Now
                            </Button>
                            <button 
                                onClick={onClose}
                                className="w-full text-center text-gray-400 text-sm hover:text-gray-600 transition-colors"
                            >
                                Maybe later
                            </button>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
};
